import React, { useEffect, useRef, useState } from "react";

const R2 = "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev";
const CANON = `${R2}/images/camera-canon-2020.webp`; // erste Kamera, 2020
const SONY = `${R2}/images/camera-sony-2021.webp`;   // erste Cinema-Kamera, 2021

// 18 Video-Ausschnitte, CHRONOLOGISCH sortiert — echte Drehdaten von Kenny (Juli 2026).
// Reihenfolge = Aufbau-Reihenfolge im Mosaik: Reihe 1 (früh) → Reihe 3 (heute).
const TILES = [
  // Reihe 1 — 2023 bis 2024
  "reel-17", // Porsche 911 Hegaublick · 2023 (ältestes)
  "reel-11", // Recruiting · Servicemitarbeiter · 2024
  "reel-5",  // Seedos · Der Snack · 2024
  "reel-18", // Club Menschmeier · Juli 2024
  "reel-15", // Mercedes G-Klasse · 2024
  "reel-16", // Porsche GT3 Donautal · 2024
  // Reihe 2 — Ende 2024 bis 2025
  "reel-8",  // VS Weihnachtsmärkte · Dez 2024
  "reel-12", // Recruiting · Fiehn · Anfang 2025
  "reel-13", // Musikvideo · 2025
  "reel-10", // Models · Editorial · 2025
  "reel-14", // Audi S8 · 2025
  "reel-6",  // Seedos · Salted · 2025
  // Reihe 3 — 2025 bis heute
  "reel-7",  // Seedos · Einbruch · 2025
  "reel-9",  // VS Abendflair · 2025
  "reel-1",  // Porsche GT3 RS Berge · 2025
  "reel-4",  // Lamborghini Urus SE · 2025
  "reel-2",  // Lamborghini Urus Dubai · 2025
  "reel-19", // Izumi E-Gym · Ende 2025 (neuestes)
].map((n) => `/thumbnails/${n}.webp`);

// Responsive: 4 Spalten zeigen 12 Tiles, 5 Spalten 15, 6 Spalten alle 18 — immer exakt 3 Reihen
const tileVisibility = (i) => (i >= 15 ? "hidden lg:block" : i >= 12 ? "hidden sm:block" : "");

const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
const ramp = (p, a, b) => clamp((p - a) / (b - a));

const useScrollProgress = (ref) => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = null;
    const update = () => {
      raf = null;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const total = r.height - vh;
      if (total <= 0) return;
      setP(clamp(-r.top / total));
    };
    const onScroll = () => { if (raf == null) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref]);
  return p;
};

const OriginStory = () => {
  const sectionRef = useRef(null);
  const p = useScrollProgress(sectionRef);

  // Kamera-Phase: Canon (2020) → crossfade → Sony (2021) → raus
  const canonOpacity = 1 - ramp(p, 0.12, 0.22);
  const sonyOpacity = ramp(p, 0.12, 0.22);
  const cameraStage = 1 - ramp(p, 0.28, 0.38);   // ganze Kamera-Bühne fadet raus
  const cameraScale = 1 + ramp(p, 0, 0.32) * 0.12;

  // Mosaik + Finale
  const mosaicDim = 1 - ramp(p, 0.76, 0.92) * 0.78;
  const logoOpacity = ramp(p, 0.78, 0.93);
  const logoScale = 0.9 + ramp(p, 0.74, 1) * 0.1;
  const headerOpacity = 1 - ramp(p, 0.74, 0.86); // Jahr/Eyebrow raus wenn Logo kommt

  // Jahr-Zähler: 2020 (Canon) → 2021 (Sony) → Mosaik-Phase 2023 → 2026
  // (synchron zu den echten Drehdaten der Kacheln: älteste 2023, neueste Ende 2025)
  const year = p < 0.12 ? 2020 : p < 0.26 ? 2021 : 2023 + Math.round(ramp(p, 0.26, 0.9) * 3);
  const camLabel = p < 0.17 ? "Die allererste Kamera · 2020" : "Erste Cinema-Kamera · 2021";

  return (
    <section
      ref={sectionRef}
      className="relative bg-background grain"
      style={{ height: "340vh" }}
      data-testid="origin-story"
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center justify-center">
        {/* Ambient-Glow */}
        <div className="pointer-events-none absolute -top-40 left-1/4 w-[45vw] h-[45vw] rounded-full bg-[hsl(var(--accent))]/20 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-40 right-1/4 w-[40vw] h-[40vw] rounded-full bg-purple-600/15 blur-[120px]" />

        {/* Jahr-Marker */}
        <div className="absolute top-10 sm:top-14 inset-x-0 z-30 text-center" style={{ opacity: headerOpacity }}>
          <div className="inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[hsl(var(--accent))]">
            <span className="w-8 h-px bg-[hsl(var(--accent))]/60" />
            Von der ersten Kamera bis heute
            <span className="w-8 h-px bg-[hsl(var(--accent))]/60" />
          </div>
          <div className="mt-3 text-6xl sm:text-8xl font-black tabular-nums text-white/90 drop-shadow-2xl">
            {year}
          </div>
        </div>

        {/* Phase 1 — Kameras (Canon → Sony) */}
        <div
          className="absolute inset-0 z-20 flex items-center justify-center"
          style={{ opacity: cameraStage, transform: `scale(${cameraScale})`, pointerEvents: "none" }}
        >
          <div className="relative w-[70%] sm:w-[42%] lg:w-[26%] max-w-sm aspect-[3/4]">
            <img
              src={CANON}
              alt="Canon EOS — die allererste Kamera, 2020"
              className="absolute inset-0 w-full h-full object-cover rounded-2xl border border-white/10 shadow-2xl"
              style={{ opacity: canonOpacity }}
            />
            <img
              src={SONY}
              alt="Sony A7III — die erste Cinema-Kamera, 2021"
              className="absolute inset-0 w-full h-full object-cover rounded-2xl border border-white/10 shadow-2xl"
              style={{ opacity: sonyOpacity }}
            />
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap px-4 py-2 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-sm text-white">
              {camLabel}
            </div>
          </div>
        </div>

        {/* Phase 2 — Mosaik: sitzt UNTER dem Jahr-Header, exakt 3 Reihen, nichts abgeschnitten.
            Aufbau chronologisch von oben (früheste Arbeiten) nach unten (heute). */}
        <div
          className="absolute left-4 right-4 sm:left-8 sm:right-8 top-48 sm:top-56 bottom-10 z-10 flex justify-center"
          style={{ opacity: mosaicDim }}
        >
          <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 grid-rows-3 gap-2 sm:gap-3 w-full max-w-6xl h-full">
            {TILES.map((src, i) => {
              const start = 0.24 + (i / TILES.length) * 0.5;
              const reveal = ramp(p, start, start + 0.12);
              return (
                <div
                  key={i}
                  className={`rounded-lg overflow-hidden bg-secondary/40 border border-white/5 ${tileVisibility(i)}`}
                  style={{ opacity: reveal, transform: `scale(${0.85 + reveal * 0.15}) translateY(${(1 - reveal) * 24}px)` }}
                >
                  <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Phase 3 — FREYS MEDIA */}
        <div
          className="absolute inset-0 z-40 flex flex-col items-center justify-center pointer-events-none"
          style={{ opacity: logoOpacity, transform: `scale(${logoScale})` }}
        >
          <div className="text-center px-4">
            <div className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight text-white drop-shadow-2xl">
              FREYS <span className="text-[hsl(var(--accent))] neon-text">MEDIA</span>
            </div>
            <p className="mt-4 text-base sm:text-xl text-white/70">
              Aus einer Kamera wurde eine Vision.
            </p>
          </div>
        </div>

        {/* Fortschritts-Balken */}
        <div className="absolute bottom-0 inset-x-0 h-1 bg-white/10 z-40">
          <div className="h-full bg-[hsl(var(--accent))] transition-[width] duration-100" style={{ width: `${p * 100}%` }} />
        </div>
      </div>
    </section>
  );
};

export default OriginStory;
